export type DecorAnimationPolicy = "auto" | "always" | "never";

/** Sur batterie, les animations jouent encore ce temps-là après le dernier geste sur la carte. */
export const BATTERY_ANIMATION_GRACE_MS = 20_000;

const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "wheel", "keydown"] as const;

export interface PowerSignals {
  reducedMotion: boolean;
  /** `null` quand le navigateur ne dit pas si l'appareil est branché (Safari, Firefox). */
  charging: boolean | null;
  inGracePeriod: boolean;
}

interface BatteryManager extends EventTarget {
  charging: boolean;
}

/** Seul un appareil dont on sait qu'il est branché joue les animations sans fin. */
export function animationsAllowed(policy: DecorAnimationPolicy, signals: PowerSignals): boolean {
  if (policy !== "auto") return policy === "always";
  if (signals.reducedMotion) return false;
  return signals.inGracePeriod || signals.charging === true;
}

/** Appelle `onChange` à chaque bascule, et une première fois dès que l'état est connu. */
export function watchAnimationsAllowed(policy: DecorAnimationPolicy, onChange: (allowed: boolean) => void): () => void {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let battery: BatteryManager | null = null;
  let stopped = false;
  let last: boolean | null = null;
  let inGracePeriod = true;
  let graceTimer = 0;
  const restartGrace = () => {
    window.clearTimeout(graceTimer);
    graceTimer = window.setTimeout(() => {
      inGracePeriod = false;
      update();
    }, BATTERY_ANIMATION_GRACE_MS);
    if (inGracePeriod) return;
    inGracePeriod = true;
    update();
  };
  restartGrace();
  for (const type of ACTIVITY_EVENTS) window.addEventListener(type, restartGrace, { passive: true });

  const update = () => {
    const allowed = animationsAllowed(policy, { reducedMotion: reducedMotion.matches, charging: battery?.charging ?? null, inGracePeriod });
    if (allowed === last) return;
    last = allowed;
    onChange(allowed);
  };

  reducedMotion.addEventListener("change", update);
  const getBattery = (navigator as Navigator & { getBattery?: () => Promise<BatteryManager> }).getBattery;
  if (policy === "auto" && getBattery) {
    getBattery.call(navigator).then((manager) => {
      if (stopped) return;
      battery = manager;
      battery.addEventListener("chargingchange", update);
      update();
    }, update);
  } else {
    update();
  }

  return () => {
    stopped = true;
    window.clearTimeout(graceTimer);
    for (const type of ACTIVITY_EVENTS) window.removeEventListener(type, restartGrace);
    reducedMotion.removeEventListener("change", update);
    battery?.removeEventListener("chargingchange", update);
  };
}
